import { Search } from 'lucide-react'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import dayjs from 'dayjs'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { fetchUnits } from '@/api/units.api'
import { fetchRobots } from '@/api/robots.api'

export interface HistoryFilterValues {
  title: string
  customerName: string
  unitId: string | undefined
  unitName?: string
  robotId: string | undefined
  robotName?: string
  dateRange: DateRangeValue | undefined
}

interface HistoryFilterPanelProps {
  values: HistoryFilterValues
  onChange: (values: HistoryFilterValues) => void
  onSearch: () => void
  showUnitFilter?: boolean
}

export function HistoryFilterPanel({
  values,
  onChange,
  onSearch,
  showUnitFilter = false,
}: HistoryFilterPanelProps) {
  const defaultDateRange: DateRangeValue = {
    from: dayjs().startOf('month').toDate(),
    to: dayjs().endOf('day').millisecond(0).toDate(),
  }

  function set<TKey extends keyof HistoryFilterValues>(
    key: TKey,
    value: HistoryFilterValues[TKey],
  ) {
    onChange({ ...values, [key]: value })
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-300 bg-white px-6 py-5"
    >
      <div className="flex flex-wrap items-end gap-4">
        {/* Input Tìm kiếm tên hội thoại */}
        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-sm font-medium">Tìm kiếm tên hội thoại</span>
          <Input
            placeholder="Nhập từ khóa"
            value={values.title}
            onChange={(e: { target: { value: string } }) =>
              set('title', e.target.value)
            }
          />
        </div>

        {/* Input Tìm kiếm tên khách hàng */}
        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-sm font-medium">Tìm kiếm tên khách hàng</span>
          <Input
            placeholder="Nhập từ khóa"
            value={values.customerName}
            onChange={(e: { target: { value: string } }) =>
              set('customerName', e.target.value)
            }
          />
        </div>

        {/* Select Robot - Có tích hợp lọc theo unitId nếu có */}
        <div className="flex min-w-200px] flex-1 flex-col gap-1">
          <span className="text-sm font-medium">Robot tiếp đón</span>
          <Select
            cacheKey={`robot-select-history-filter-${values.unitId ?? 'all'}`}
            initialPage={0}
            lazy
            placeholder="Tất cả robot"
            allowClear
            value={values.robotId ?? undefined}
            className="w-full"
            enableRemoteSearch
            allLabel="Tất cả robot"
            initialOptions={
              values.robotId && values.robotName
                ? [{ value: values.robotId, label: values.robotName }]
                : []
            }
            loadOptions={async ({ page, signal, searchText }) => {
              const res = await fetchRobots(
                page,
                10,
                {
                  robotId: searchText?.trim() || undefined,
                  unitId: values.unitId,
                },
                signal,
              )
              const arr = Array.isArray(res.items) ? res.items : []
              const pageSize = 10
              const hasNextPage = arr.length >= pageSize

              return {
                options: arr.map((u: any) => ({
                  value: u.id,
                  label: u.name,
                })),
                hasNextPage,
                nextPage: hasNextPage ? page + 1 : undefined,
              }
            }}
            onLazyValueChange={(robotId, option) => {
              onChange({
                ...values,
                robotId: robotId,
                robotName: option?.label ? String(option.label) : undefined,
              })
            }}
          />
        </div>

        {/* Select Đơn vị (Hiển thị tùy quyền) */}
        {showUnitFilter && (
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Đơn vị</span>
            <Select
              cacheKey="history-filter-unit-select"
              initialPage={0}
              lazy
              placeholder="Tất cả đơn vị"
              allowClear
              value={values.unitId ?? undefined}
              className="w-full"
              enableRemoteSearch
              allLabel="Tất cả đơn vị"
              initialOptions={
                values.unitId && values.unitName
                  ? [{ value: values.unitId, label: values.unitName }]
                  : []
              }
              loadOptions={async ({ page, signal, searchText }) => {
                const res = await fetchUnits(
                  page,
                  10,
                  { keySearch: searchText?.trim() || undefined },
                  signal,
                )
                const arr = Array.isArray(res.items) ? res.items : []
                const pageSize = 10
                const hasNextPage = arr.length >= pageSize

                return {
                  options: arr.map((u: any) => ({
                    value: u.id,
                    label: u.name,
                  })),
                  hasNextPage,
                  nextPage: hasNextPage ? page + 1 : undefined,
                }
              }}
              onLazyValueChange={(unitId, option) => {
                onChange({
                  ...values,
                  unitId: unitId,
                  unitName: option?.label ?? undefined,
                  robotId: undefined,
                  robotName: undefined,
                })
              }}
            />
          </div>
        )}

        {/* DatePicker */}
        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-sm font-medium">Ngày thực hiện</span>
          <DateRangePicker
            value={values.dateRange ?? defaultDateRange}
            onChange={(v) => set('dateRange', v)}
            className="w-full"
          />
        </div>

        {/* Button Tìm kiếm */}
        <Button
          variant="outline"
          size="md"
          className="border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
          onClick={onSearch}
        >
          <Search size={16} />
          Tìm kiếm
        </Button>
      </div>
    </FilterFormWrapper>
  )
}
